import TextField from "./WorksheetTextField";
import { Fragment, useState } from "react";
import { Box, IconButton, Tooltip, Menu, MenuItem, Typography } from "@mui/material";
import RestartAlt from "@mui/icons-material/RestartAlt";
import WorksheetInfoTooltip from "./WorksheetInfoTooltip";
import Add from "@mui/icons-material/Add";
import WorksheetMaterialLibraryDrawer from "./WorksheetMaterialLibraryDrawer";
import WorksheetLibraryDrawer, { LibraryCategory } from "./WorksheetLibraryDrawer";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import Tune from "@mui/icons-material/Tune";
import UiCard from "components/base/UiCard";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import {
  calculateWorksheet,
  CostRow,
  Worksheet,
  money,
  truckingUnits,
  changeTruckingUnit,
  TruckingUnit,
  materialUnits,
} from "./calculator";
import WorksheetMaterialCalculationDrawer from "./WorksheetMaterialCalculationDrawer";
import { worksheetNumberProps, worksheetCompactFieldSx } from "./worksheetStyles";

const unitOptions: Partial<Record<CostRow["category"], string[]>> = {
  labor: ["Hours", "Days", "Half Day", "Job", "Each"],
  trucking: truckingUnits,
  equipment: ["Hours", "Days", "Half Day", "Mileage", "Each"],
  material: materialUnits,
};

const titles = {
  labor: "Labor",
  material: "Materials",
  trucking: "Trucking",
  equipment: "Equipment",
  other: "Other costs",
};
// The same tracks and cell padding are used for every header and editable row.
const columns = {
  xs: "minmax(160px, 1fr) 140px 180px 132px 96px 110px 80px",
  md: "minmax(160px, 1fr) 124px 148px 132px 96px 110px 80px",
};
const fieldColumns = {
  name: 1,
  trucksNeeded: 2,
  sourcePlantName: 2,
  quantity: 3,
  unit: 4,
  rate: 5,
};

type Props = {
  category: CostRow["category"];
  rows: CostRow[];
  totals?: ReturnType<typeof calculateWorksheet>;
  readOnly: boolean;
  busy: boolean;
  settings: any;
  item: Worksheet;
  onItemChange: (patch: Partial<Worksheet>) => void;
  onChange: (index: number, patch: Partial<CostRow>) => void;
  onAdd: () => void;
  onMove: (index: number, offset: number) => void;
  onCopy: (index: number) => void;
  onDelete: (index: number) => void;
};
export default function WorksheetCostSection({
  category,
  rows,
  totals,
  readOnly,
  busy,
  settings,
  item,
  onItemChange,
  onChange,
  onAdd,
  onMove,
  onCopy,
  onDelete,
}: Props) {
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const hasLibrary = ["labor", "equipment", "other", "trucking", "material"].includes(category);
  const units = unitOptions[category];
  const trucking = category === "trucking";
  const grid = {
    display: "grid",
    gridTemplateColumns: columns,
    alignItems: "center",
    minWidth: { xs: 920, md: 850 },
  };
  const fields = trucking
    ? (["name", "trucksNeeded", "quantity", "unit", "rate"] as const)
    : category === "material"
    ? (["name", "sourcePlantName", "quantity", "unit", "rate"] as const)
    : (["name", "quantity", "unit", "rate"] as const);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; index: number } | null>(null);
  const [helper, setHelper] = useState<number | null>(null);
  const matching = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.category === category);
  const total = matching.reduce(
    (sum, { index }) => sum + (totals?.rows[index].total.toNumber() ?? 0),
    0
  );
  const action = (fn: (index: number) => void) => {
    if (menu) fn(menu.index);
    setMenu(null);
    setHelper(null);
  };
  return (
    <UiCard variant="section" sx={worksheetCompactFieldSx}>
      <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)}>
        <MenuItem
          disabled={busy || readOnly}
          onClick={() => {
            setAddAnchor(null);
            onAdd();
          }}
        >
          Add Custom
        </MenuItem>
        <MenuItem
          disabled={busy || readOnly}
          onClick={() => {
            setAddAnchor(null);
            setLibraryOpen(true);
          }}
        >
          Add from Library
        </MenuItem>
      </Menu>
      {libraryOpen && category === "material" && !readOnly && (
        <WorksheetMaterialLibraryDrawer
          item={item}
          settings={settings}
          disabled={busy}
          onClose={() => setLibraryOpen(false)}
          onAdd={(row) => onItemChange({ rows: [...item.rows, row] })}
        />
      )}
      {libraryOpen && hasLibrary && category !== "material" && !readOnly && (
        <WorksheetLibraryDrawer
          category={category as LibraryCategory}
          disabled={busy}
          onClose={() => setLibraryOpen(false)}
          onAdd={(row) => onItemChange({ rows: [...item.rows, row] })}
        />
      )}

      <Box
        sx={{ overflowX: "auto" }}
        role="region"
        aria-label={`${titles[category]} cost table`}
        tabIndex={0}
      >
        <Box sx={{ ...grid, py: 0.25, minHeight: { xs: 44, md: 30 } }}>
          <Typography sx={{ gridColumn: "1 / -3", px: 0.75, fontSize: 13, fontWeight: 700 }}>
            {titles[category]}{" "}
            <Box
              component="span"
              sx={{ color: draftColors.textSecondary, fontSize: 12, fontWeight: 400 }}
            >
              ({matching.length})
            </Box>
          </Typography>
          <Typography
            data-worksheet-subtotal={category}
            sx={{
              gridColumn: "-3 / -2",
              px: 0.75,
              textAlign: "right",
              fontSize: 13,
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {totals ? money(total) : "—"}
          </Typography>
          <Box sx={{ gridColumn: "-2 / -1", display: "flex", justifyContent: "center" }}>
            {!readOnly && (
              <IconButton
                size="small"
                disabled={busy}
                aria-label={`Add ${titles[category]}`}
                aria-haspopup={hasLibrary ? "menu" : undefined}
                aria-expanded={hasLibrary ? !!addAnchor : undefined}
                sx={{ color: draftColors.green }}
                onClick={(event) => (hasLibrary ? setAddAnchor(event.currentTarget) : onAdd())}
              >
                <Add fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
        {matching.length > 0 && (
          <Box role="table" aria-label={`${titles[category]} costs`}>
            <Box
              role="row"
              sx={{
                ...grid,
                bgcolor: draftColors.surfaceTableHeader,
                borderTop: `1px solid ${draftColors.border}`,
                borderBottom: `1px solid ${draftColors.border}`,
              }}
            >
              {(trucking
                ? [
                    "Cost description",
                    "Trucks Needed",
                    "Quantity",
                    "Unit",
                    "Rate ($)",
                    "Cost total",
                    "Actions",
                  ]
                : category === "material"
                ? [
                    "Cost description",
                    "Plant",
                    "Quantity",
                    "Unit",
                    "Rate ($)",
                    "Cost total",
                    "Actions",
                  ]
                : ["Cost description", "Quantity", "Unit", "Rate ($)", "Cost total", "Actions"]
              ).map((label, i) => (
                <Box
                  role="columnheader"
                  key={label}
                  sx={{
                    gridColumn: trucking || category === "material" || i === 0 ? i + 1 : i + 2,
                    px: 0.75,
                    py: 0.5,
                    textAlign: ["Quantity", "Trucks Needed", "Rate ($)", "Cost total"].includes(
                      label
                    )
                      ? "right"
                      : "left",
                    ...(label === "Quantity"
                      ? { pr: { xs: "88px", md: "56px" } }
                      : label === "Trucks Needed"
                      ? { pr: { xs: "48px", md: "32px" } }
                      : {}),
                    fontSize: 11,
                    fontWeight: 600,
                    color: draftColors.textLabel,
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>
            {matching.map(({ row, index }) => (
              <Fragment key={index}>
                <Box
                  role="row"
                  sx={{ ...grid, py: 0.25, borderBottom: `1px solid ${draftColors.borderSoft}` }}
                >
                  {fields.map((key) => (
                    <Box
                      role="cell"
                      key={key}
                      sx={{
                        gridColumn: fieldColumns[key],
                        px: 0.75,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.25,
                      }}
                    >
                      <Tooltip title={key === "sourcePlantName" ? row.sourcePlantName ?? "" : ""}>
                        <TextField
                          preserveBlank={key === "trucksNeeded" && row.truckingUnit !== "Hours"}
                          overridden={
                            key === "quantity" &&
                            row.calculation !== "manual" &&
                            row.quantityOverride != null
                          }
                          size="small"
                          fullWidth
                          sx={{ flex: "1 1 0", minWidth: 0 }}
                          type="text"
                          select={key === "unit" && !!units}
                          inputProps={{
                            "aria-label": `${key} for cost ${index + 1}`,
                            ...(["quantity", "trucksNeeded", "rate"].includes(key)
                              ? { ...worksheetNumberProps, inputMode: "decimal" as const }
                              : {}),

                            style: {
                              textAlign: ["quantity", "trucksNeeded", "rate"].includes(key)
                                ? "right"
                                : "left",
                            },
                          }}
                          InputProps={{ readOnly: key === "sourcePlantName" }}
                          disabled={readOnly || busy}
                          value={
                            key === "quantity" && row.calculation !== "manual"
                              ? row.quantityOverride ??
                                totals?.rows[index]?.quantity.toDecimalPlaces(6).toString() ??
                                ""
                              : row[key] ?? ""
                          }
                          onChange={(e) =>
                            onChange(
                              index,
                              trucking && key === "unit"
                                ? changeTruckingUnit(row, e.target.value as TruckingUnit)
                                : {
                                    ...(trucking && key === "trucksNeeded"
                                      ? { truckCountDefault: false }
                                      : {}),
                                    [key === "quantity" && row.calculation !== "manual"
                                      ? "quantityOverride"
                                      : key]: e.target.value,
                                  }
                            )
                          }
                        >
                          {key === "unit" &&
                            units && [
                              ...(!units.includes(row.unit)
                                ? [
                                    <MenuItem key="saved-unit" value={row.unit} disabled>
                                      {row.unit}
                                    </MenuItem>,
                                  ]
                                : []),
                              ...units
                                .filter(
                                  (unit) =>
                                    !["density", "thickness_factor"].includes(row.calculation) ||
                                    ["Ton", "Pound"].includes(unit)
                                )
                                .map((unit) => (
                                  <MenuItem key={unit} value={unit}>
                                    {unit}
                                  </MenuItem>
                                )),
                            ]}
                        </TextField>
                      </Tooltip>
                      {["name", "quantity", "trucksNeeded"].includes(key) && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexShrink: 0,
                            width: {
                              xs: key === "quantity" ? 80 : 40,
                              md: key === "quantity" ? 48 : 24,
                            },
                          }}
                        >
                          {key === "name" && row.sourceType && (
                            <WorksheetInfoTooltip
                              label={`Library source for cost ${index + 1}`}
                              text={`${
                                row.sourceType === "crew"
                                  ? "Crew"
                                  : row.sourceType === "plant_product"
                                  ? "Plant Product"
                                  : row.sourceType === "transfer_truck"
                                  ? "Transfer Truck"
                                  : row.sourceType === "equipment"
                                  ? "Equipment"
                                  : "Additional Cost"
                              } library: ${row.sourceName ?? row.name}${
                                row.sourceType === "transfer_truck" && row.truckCapacity
                                  ? ` · Capacity: ${row.truckCapacity} US tons`
                                  : ""
                              }`}
                            />
                          )}
                          {key === "trucksNeeded" && row.truckingUnit !== "Hours" && (
                            <WorksheetInfoTooltip
                              label={`Truck planning count for cost ${index + 1}`}
                              text="For planning only; does not affect cost."
                            />
                          )}
                          {key === "quantity" && (trucking || row.calculation !== "manual") && (
                            <WorksheetInfoTooltip
                              label={`Quantity formula for cost ${index + 1}`}
                              overridden={row.quantityOverride != null}
                              text={`${
                                totals?.rows[index]?.formula ??
                                "Fix calculation inputs to see the formula."
                              }${
                                row.quantityOverride != null
                                  ? ` · Using manual override: ${row.quantityOverride} ${row.unit}`
                                  : totals && !trucking
                                  ? ` = ${totals.rows[index].quantity.toDecimalPlaces(6)} ${
                                      row.unit
                                    }`
                                  : ""
                              }`}
                            />
                          )}
                          {key === "quantity" &&
                            row.calculation !== "manual" &&
                            row.quantityOverride != null &&
                            !readOnly && (
                              <Tooltip title="Restore calculation">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={busy}
                                    aria-label={`Restore calculation for cost ${index + 1}`}
                                    onClick={() => onChange(index, { quantityOverride: null })}
                                  >
                                    <RestartAlt sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                        </Box>
                      )}
                    </Box>
                  ))}
                  <Box
                    role="cell"
                    sx={{
                      px: 0.75,
                      textAlign: "right",
                      fontSize: 13,
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {totals ? money(totals.rows[index].total) : "—"}
                  </Box>
                  <Box role="cell" sx={{ display: "flex", justifyContent: "center" }}>
                    {category === "material" && (
                      <IconButton
                        size="small"
                        aria-label={`Quantity calculation for cost ${index + 1}`}
                        aria-expanded={helper === index}
                        onClick={() => setHelper(helper === index ? null : index)}
                      >
                        <Tune sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                    {!readOnly && (
                      <IconButton
                        size="small"
                        disabled={busy}
                        aria-label={`Actions for cost ${index + 1}`}
                        onClick={(e) => setMenu({ anchor: e.currentTarget, index })}
                      >
                        <MoreHoriz sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {category === "material" &&
                  helper === index &&
                  (row.sourceType === "plant_product" && !readOnly ? (
                    <WorksheetMaterialLibraryDrawer
                      initialRow={row}
                      item={item}
                      settings={settings}
                      disabled={busy}
                      onClose={() => setHelper(null)}
                      onAdd={(updated) => onChange(index, updated)}
                    />
                  ) : (
                    <WorksheetMaterialCalculationDrawer
                      item={item}
                      row={row}
                      onApply={(patch) => onChange(index, patch)}
                      onClose={() => setHelper(null)}
                      readOnly={readOnly}
                      busy={busy}
                      settings={settings}
                    />
                  ))}
              </Fragment>
            ))}
          </Box>
        )}
      </Box>
      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => action(onCopy)}>Duplicate cost</MenuItem>
        <MenuItem
          disabled={menu?.index === matching[0]?.index}
          onClick={() =>
            action((i) =>
              onMove(i, matching[matching.findIndex((entry) => entry.index === i) - 1].index - i)
            )
          }
        >
          Move up
        </MenuItem>
        <MenuItem
          disabled={menu?.index === matching[matching.length - 1]?.index}
          onClick={() =>
            action((i) =>
              onMove(i, matching[matching.findIndex((entry) => entry.index === i) + 1].index - i)
            )
          }
        >
          Move down
        </MenuItem>
        {Object.entries(titles)
          .filter(([key]) => key !== category)
          .map(([key, label]) => (
            <MenuItem
              key={key}
              onClick={() =>
                action((i) =>
                  onChange(i, { category: key as CostRow["category"], calculation: "manual" })
                )
              }
            >
              Move to {label.toLowerCase()}
            </MenuItem>
          ))}
        <MenuItem sx={{ color: draftColors.red }} onClick={() => action(onDelete)}>
          Delete cost
        </MenuItem>
      </Menu>
    </UiCard>
  );
}
